(function () {
  if (!customElements.get('common-table')) {
    class CommonTable extends HTMLElement {
      constructor() {
        super();
        this.initialized = false;
        this.expanded = false;
        this.hasManualToggle = false;
        this.toggle = this.toggle.bind(this);
        this.init = this.init.bind(this);
        this.handleMobileShowChange = this.handleMobileShowChange.bind(this);
      }

      connectedCallback() {
        if (window.elToObserver) {
          window.elToObserver(this, this.init);
        } else {
          this.init();
        }
      }

      disconnectedCallback() {
        if (this.toggleButton) {
          this.toggleButton.removeEventListener('click', this.toggle);
        }
        if (this.mobileShowMedia) {
          if (this.mobileShowMedia.removeEventListener) {
            this.mobileShowMedia.removeEventListener('change', this.handleMobileShowChange);
          } else if (this.mobileShowMedia.removeListener) {
            this.mobileShowMedia.removeListener(this.handleMobileShowChange);
          }
        }
        this.initialized = false;
      }

      init() {
        if (this.initialized) {
          return;
        }

        this.initialized = true;
        this.groups = Array.from(this.querySelectorAll('[data-table-group]'));
        this.toggleButton = this.querySelector('[data-common-table-toggle]');
        this.toggleLabel = this.querySelector('[data-toggle-label]');
        this.overlay = this.querySelector('[data-table-overlay]');
        this.mobileShow = this.dataset.mobileShow === 'true';
        this.mobileShowMedia = window.matchMedia ? window.matchMedia('(max-width: 768px)') : null;
        this.initialVisibleGroups = Math.max(
          0,
          parseInt(this.dataset.initialVisibleGroups || '1', 10) || 1
        );
        this.expanded = this.shouldDefaultExpand();

        if (this.toggleButton) {
          this.toggleButton.addEventListener('click', this.toggle);
        }

        if (this.mobileShowMedia) {
          if (this.mobileShowMedia.addEventListener) {
            this.mobileShowMedia.addEventListener('change', this.handleMobileShowChange);
          } else if (this.mobileShowMedia.addListener) {
            this.mobileShowMedia.addListener(this.handleMobileShowChange);
          }
        }

        this.updateState(false);
      }

      toggle() {
        this.hasManualToggle = true;
        this.expanded = !this.expanded;
        this.updateState(true);
      }

      shouldDefaultExpand() {
        return Boolean(this.mobileShow && this.mobileShowMedia && this.mobileShowMedia.matches);
      }

      handleMobileShowChange() {
        if (this.hasManualToggle) {
          return;
        }

        this.expanded = this.shouldDefaultExpand();
        this.updateState(false);
      }

      isRowsOpen(rows) {
        return !rows.hidden && rows.dataset.open === 'true';
      }

      finishRowsTransition(rows, isOpen, keepGroupVisible) {
        clearTimeout(rows._commonTableTransitionTimer);
        rows.removeEventListener('transitionend', rows._commonTableTransitionEnd);
        rows.style.height = isOpen ? 'auto' : '0px';
        rows.hidden = !isOpen;

        const group = rows.closest('[data-table-group]');
        if (group) {
          group.hidden = !(isOpen || keepGroupVisible);
        }
      }

      setRowsOpen(rows, isOpen, animate, keepGroupVisible) {
        const group = rows.closest('[data-table-group]');
        const wasOpen = this.isRowsOpen(rows);
        const currentHeight = rows.hidden ? 0 : rows.getBoundingClientRect().height;
        rows.removeEventListener('transitionend', rows._commonTableTransitionEnd);
        clearTimeout(rows._commonTableTransitionTimer);

        rows.dataset.open = isOpen ? 'true' : 'false';
        if (isOpen) {
          if (group) {
            group.hidden = false;
          }

          if (wasOpen) {
            rows.hidden = false;
            rows.setAttribute('aria-hidden', 'false');
            rows.classList.remove('is-collapsed');
            rows.classList.add('is-expanded');
            rows.style.height = 'auto';
            return;
          }

          rows.hidden = false;
          rows.setAttribute('aria-hidden', 'false');
          rows.classList.remove('is-collapsed');
          rows.classList.add('is-expanded');

          if (!animate) {
            rows.style.height = 'auto';
            return;
          }

          rows.style.height = `${currentHeight}px`;
          rows.offsetHeight;

          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              rows.style.height = `${rows.scrollHeight}px`;
            });
          });

          rows._commonTableTransitionEnd = (event) => {
            if (event.target !== rows || event.propertyName !== 'height') {
              return;
            }
            this.finishRowsTransition(rows, true);
          };
          rows.addEventListener('transitionend', rows._commonTableTransitionEnd);
          rows._commonTableTransitionTimer = setTimeout(() => {
            this.finishRowsTransition(rows, true);
          }, 560);
          return;
        }

        if (!wasOpen) {
          rows.setAttribute('aria-hidden', 'true');
          rows.classList.remove('is-expanded');
          rows.classList.add('is-collapsed');
          rows.style.height = '0px';
          rows.hidden = true;
          if (group) {
            group.hidden = !keepGroupVisible;
          }
          return;
        }

        rows.setAttribute('aria-hidden', 'true');
        rows.classList.remove('is-expanded');
        rows.classList.add('is-collapsed');

        if (!animate) {
          rows.style.height = '0px';
          rows.hidden = true;
          if (group) {
            group.hidden = !keepGroupVisible;
          }
          return;
        }

        rows.hidden = false;
        rows.style.height = `${currentHeight || rows.scrollHeight}px`;
        rows.offsetHeight;

        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            rows.style.height = '0px';
          });
        });

        rows._commonTableTransitionEnd = (event) => {
          if (event.target !== rows || event.propertyName !== 'height') {
            return;
          }
          this.finishRowsTransition(rows, false, keepGroupVisible);
        };
        rows.addEventListener('transitionend', rows._commonTableTransitionEnd);
        rows._commonTableTransitionTimer = setTimeout(() => {
          this.finishRowsTransition(rows, false, keepGroupVisible);
        }, 560);
      }

      updateState(animate) {
        const shouldAnimate = animate && !(
          window.matchMedia
          && window.matchMedia('(prefers-reduced-motion: reduce)').matches
        );

        this.groups.forEach((group, index) => {
          const rows = group.querySelector('[data-table-group-rows]');
          if (!rows) {
            return;
          }

          const groupVisible = this.expanded || index <= this.initialVisibleGroups;
          this.setRowsOpen(rows, this.expanded || index < this.initialVisibleGroups, shouldAnimate, groupVisible);
        });

        if (!this.toggleButton) {
          if (this.overlay) {
            this.overlay.hidden = this.expanded;
            this.overlay.setAttribute('aria-hidden', this.expanded ? 'true' : 'false');
          }
          return;
        }

        this.toggleButton.setAttribute('aria-expanded', this.expanded ? 'true' : 'false');

        if (this.overlay) {
          this.overlay.hidden = this.expanded;
          this.overlay.setAttribute('aria-hidden', this.expanded ? 'true' : 'false');
        }

        if (this.toggleLabel) {
          const label = this.expanded
            ? this.toggleLabel.dataset.collapseLabel
            : this.toggleLabel.dataset.expandLabel;
          this.toggleLabel.innerHTML = '<span class="visually-hidden">' + label + '</span>';
        }
      }
    }

    customElements.define('common-table', CommonTable);
  }
})();
